interface EquipmentItem {
  availability: string;
  group: string;
  name: string;
  attribute: string;
  value: string;
  location: string;
}

export interface Automobile extends Record<string, unknown> {
  equipments: EquipmentItem[];
}

export interface VehicleData {
  bumperResponse: {
    vehicles: {
      automobiles: Automobile[];
    };
  };
}

interface FeatureBucket {
  brand: string | null;
  type: string | null;
  locations: Set<string>;
  capabilities: Set<string>;
  counts: Map<string, number | string>;
  specs: Map<string, string | number>;
  performance: Map<string, string | number>;
  metrics: Map<string, string>;
  presence: boolean;
  extras: Array<{ attribute: string; value: string }>;
}

interface ProcessedFeature {
  title: string;
  details: string[];
  score: number;
}

interface GroupedFeatures {
  [group: string]: ProcessedFeature[];
}

export class VehicleEquipmentConverter {
  private readonly COMMON = {
    STANDARD: 'standard',
    YES: 'yes',
    NO: 'no',
    UNKNOWN: 'unknown',
    EMPTY: '',
    STANDARD_FEATURE: 'Standard feature',
    TYPE_PREFIX: 'Type: ',
    WARRANTY_PREFIX: 'Warranty: ',
    RANGE_PREFIX: 'Range: ',
    SPEAKERS_SUFFIX: ' speakers',
    MONTH_SUBSCRIPTION: '-month subscription included',
    WHEELS_PREFIX: 'Wheels: ',
    TIRES_PREFIX: 'Tires: ',
    ALLOY_SUFFIX: ' alloy',
    SEPARATOR: '::',
    FRONT: 'front',
    REAR: 'rear',
    DRIVER: 'driver',
    PASSENGER: 'passenger',
  };

  private readonly CANONICAL_KEYS = {
    BRAND: 'brand',
    TYPE: 'type',
    LOCATIONS: 'locations',
    CAPABILITIES: 'capabilities',
    COUNTS: 'counts',
    SPECS: 'specs',
    PERFORMANCE: 'performance',
    METRICS: 'metrics',
  };

  private readonly COUNT_NAMES = {
    SPEAKERS: 'speakers',
    MONTHS: 'months',
    AIRBAGS: 'airbags',
  };

  private readonly PERFORMANCE_KEYS = {
    MAX_POWER_HP_PS: 'Maximum power hp/PS',
    MAX_POWER_HP: 'maximum power hp',
    MAX_POWER_KW: 'Maximum power kW',
    MAX_POWER_KW_LOWER: 'maximum power kw',
    RPM_LOW: 'rpm for maximum power (low)',
    RPM_TORQUE_LOW: 'rpm for maximum torque (low)',
  };

  private readonly SPEC_KEYS = {
    TIRE_WIDTH: 'tire width',
    TIRE_PROFILE: 'tire profile',
    DIAMETER: 'diameter',
    RIM_WIDTH: 'rim width (in)',
  };

  private readonly CATEGORY_ORDER = [
    'Safety & Driver Assist',
    'Comfort & Convenience',
    'Exterior',
    'Interior',
    'Infotainment',
    'Engine',
    'Transmission',
    'Dimensions',
    'Warranty',
    'General',
    'Pricing',
    'Fuel Economy',
    'Suspension',
    'Hybrid & Electric',
  ];

  private readonly CAPABILITIES_KEYS = [
    'operation',
    'includes',
    'heated',
    'cooled',
    'touch screen',
    'keyless',
    'automatic',
    'electric',
  ];

  private readonly SPECS_KEYS = ['diameter', 'width', 'profile', 'rim width'];

  private readonly PERF_KEYS = ['hp', 'kw', 'torque', 'rpm', 'maximum power'];
  private readonly METRICS_KEYS = ['warranty', 'distance', 'duration', 'capacity', 'range', 'fuel economy', 'miles', 'months'];

  // normalize attribute names to canonical keys
  private normalizeAttribute(attribute: string): string {
    const normalized = attribute.toLowerCase().trim();

    // Canonical mappings based on requirements
    if (normalized === 'manufacturer\'s name' || normalized === 'manufacturer name') return this.CANONICAL_KEYS.BRAND;
    if (normalized === 'type') return this.CANONICAL_KEYS.TYPE;
    if (normalized === 'location') return this.CANONICAL_KEYS.LOCATIONS;

    // Boolean capabilities
    if (this.CAPABILITIES_KEYS.some((keyword) => normalized.includes(keyword))) return this.CANONICAL_KEYS.CAPABILITIES;

    // Counts
    if (normalized.includes('number') || normalized.includes('count')) return this.CANONICAL_KEYS.COUNTS;

    // Specs (sizes/dimensions)
    if (this.SPECS_KEYS.some((keyword) => normalized.includes(keyword))) return this.CANONICAL_KEYS.SPECS;

    // Performance figures
    if (this.PERF_KEYS.some((keyword) => normalized.includes(keyword))) return this.CANONICAL_KEYS.PERFORMANCE;

    // Metrics (warranty/fuel/dimensions with units)
    if (this.METRICS_KEYS.some((keyword) => normalized.includes(keyword))) return this.CANONICAL_KEYS.METRICS;

    return normalized;
  }

  private parseNumeric(value: string): number | string {
    const trimmed = value.trim();
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      return parseFloat(trimmed);
    }
    return trimmed;
  }

  private createFeatureBucket(): FeatureBucket {
    return {
      brand: null,
      type: null,
      locations: new Set(),
      capabilities: new Set(),
      counts: new Map(),
      specs: new Map(),
      performance: new Map(),
      metrics: new Map(),
      presence: false,
      extras: [],
    };
  }

  private ingestAttribute(bucket: FeatureBucket, equipment: EquipmentItem): void {
    const attribute = equipment.attribute.trim();
    const value = equipment.value.trim();
    const location = equipment.location.trim();
    const name = equipment.name.trim();
    const canonical = this.normalizeAttribute(attribute);

    if (attribute.toLowerCase() === name.toLowerCase() && value === this.COMMON.STANDARD) {
      bucket.presence = true;
      return;
    }

    // Process by canonical type
    switch (canonical) {
      case this.CANONICAL_KEYS.BRAND:
        bucket.brand = value;
        break;

      case this.CANONICAL_KEYS.TYPE:
        if (value !== this.COMMON.STANDARD) {
          bucket.type = value;
        }
        break;

      case this.CANONICAL_KEYS.LOCATIONS:
        if (location) bucket.locations.add(location);
        if (value && value !== this.COMMON.STANDARD) bucket.locations.add(value);
        break;

      case this.CANONICAL_KEYS.CAPABILITIES:
        if (value.toLowerCase() === this.COMMON.YES) {
          bucket.capabilities.add(this.humanizeCapability(attribute));
        }
        break;

      case this.CANONICAL_KEYS.COUNTS:
        bucket.counts.set(this.normalizeCountName(attribute), this.parseNumeric(value));
        break;

      case this.CANONICAL_KEYS.SPECS:
        bucket.specs.set(attribute, this.parseNumeric(value));
        break;

      case this.CANONICAL_KEYS.PERFORMANCE:
        bucket.performance.set(attribute, this.parseNumeric(value));
        break;

      case this.CANONICAL_KEYS.METRICS:
        bucket.metrics.set(attribute, value);
        break;

      default:
        // Else push raw pair into extras
        if (value !== this.COMMON.STANDARD && value !== this.COMMON.EMPTY) {
          bucket.extras.push({ attribute, value });
        }
        break;
    }

    // Always add location if present
    if (location && location !== this.COMMON.UNKNOWN && location !== this.COMMON.EMPTY) {
      bucket.locations.add(location);
    }
  }

  private humanizeCapability(attribute: string): string {
    return attribute
      .replace(/^includes\s+/i, this.COMMON.EMPTY)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private normalizeCountName(attribute: string): string {
    const lower = attribute.toLowerCase();
    if (lower.includes('speaker')) return this.COUNT_NAMES.SPEAKERS;
    if (lower.includes('month')) return this.COUNT_NAMES.MONTHS;
    if (lower.includes('airbag')) return this.COUNT_NAMES.AIRBAGS;
    return attribute;
  }

  // Synthesize feature title
  private synthesizeTitle(name: string, bucket: FeatureBucket): string {
    const baseName = bucket.brand || name;

    if (bucket.locations.size === 0) {
      return baseName;
    }

    const locationArray = Array.from(bucket.locations).filter(
      (loc) => loc && loc !== this.COMMON.UNKNOWN && loc !== this.COMMON.EMPTY
    );

    if (locationArray.length === 0) {
      return baseName;
    }

    // If exactly two and they're complementary (front/rear): "front & rear"
    if (locationArray.length === 2) {
      const sorted = locationArray.sort();
      if (
        (sorted[0] === this.COMMON.FRONT && sorted[1] === this.COMMON.REAR) ||
        (sorted[0] === this.COMMON.DRIVER && sorted[1] === this.COMMON.PASSENGER)
      ) {
        return `${baseName} (${sorted[0]} & ${sorted[1]})`;
      }
    }

    return `${baseName} (${locationArray.join(', ')})`;
  }

  //  Synthesize detail lines
  private synthesizeDetails(bucket: FeatureBucket): string[] {
    const details: string[] = [];

    const formatDetail = (key: string, value: string | number): string => {
      const formattedKey = key
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    
      const lowerStringValue = String(value).toLowerCase();
    
      return lowerStringValue === this.COMMON.YES ? formattedKey : `${formattedKey}: ${value}`;
    };
    
    //  Type / capability
    if (bucket.type && bucket.type !== this.COMMON.STANDARD) {
      details.push(`${this.COMMON.TYPE_PREFIX}${bucket.type}`);
    }

    // Add capabilities (sorted, humanized, de-duplicated)
    const capabilities = Array.from(bucket.capabilities).sort();
    if (capabilities.length > 0) {
      details.push(...capabilities);
    }

    //  Add specs (including spring type, stabilizer bar, etc.)
    for (const [key, value] of bucket.specs) {
      const keyLower = key.toLowerCase();

      // Skip if we're handling this in a combined format below
      if (
        keyLower.includes(this.SPEC_KEYS.TIRE_WIDTH) ||
        keyLower.includes(this.SPEC_KEYS.TIRE_PROFILE) ||
        (keyLower === this.SPEC_KEYS.DIAMETER &&
          (bucket.specs.has(this.SPEC_KEYS.TIRE_WIDTH) || bucket.specs.has(this.SPEC_KEYS.RIM_WIDTH)))
      ) {
        continue;
      }

      details.push(formatDetail(key, value));
    }

    //  Add joined specs - Tires special handling
    const tireWidth = bucket.specs.get(this.SPEC_KEYS.TIRE_WIDTH);
    const tireProfile = bucket.specs.get(this.SPEC_KEYS.TIRE_PROFILE);
    const tireDiameter = bucket.specs.get(this.SPEC_KEYS.DIAMETER);

    if (tireWidth && tireProfile && tireDiameter) {
      details.push(`${this.COMMON.TIRES_PREFIX}${tireWidth}/${tireProfile} on ${tireDiameter}" wheels`);
    } else {
      // Wheels: rim width & diameter
      const rimWidth = bucket.specs.get(this.SPEC_KEYS.RIM_WIDTH);
      const rimDiameter = bucket.specs.get(this.SPEC_KEYS.DIAMETER);
      if (rimWidth && rimDiameter) {
        details.push(`${this.COMMON.WHEELS_PREFIX}${rimDiameter}" x ${rimWidth}"${this.COMMON.ALLOY_SUFFIX}`);
      }
    }

    // Add counts
    for (const [key, value] of bucket.counts) {
      if (key === this.COUNT_NAMES.SPEAKERS) {
        details.push(`${value}${this.COMMON.SPEAKERS_SUFFIX}`);
      } else if (key === this.COUNT_NAMES.MONTHS) {
        details.push(`${value}${this.COMMON.MONTH_SUBSCRIPTION}`);
      } else {
        details.push(formatDetail(key, value));
      }
    }

    //  Add performance
    const hp =
      bucket.performance.get(this.PERFORMANCE_KEYS.MAX_POWER_HP_PS) || bucket.performance.get(this.PERFORMANCE_KEYS.MAX_POWER_HP);
    const kw =
      bucket.performance.get(this.PERFORMANCE_KEYS.MAX_POWER_KW) ||
      bucket.performance.get(this.PERFORMANCE_KEYS.MAX_POWER_KW_LOWER);
    const rpm =
      bucket.performance.get(this.PERFORMANCE_KEYS.RPM_LOW) || bucket.performance.get(this.PERFORMANCE_KEYS.RPM_TORQUE_LOW);

    if (hp || kw) {
      let perfLine = this.COMMON.EMPTY;
      if (hp && kw) {
        perfLine = `${hp} hp (${kw} kW)`;
      } else if (hp) {
        perfLine = `${hp} hp`;
      } else {
        perfLine = `${kw} kW`;
      }
      if (rpm) {
        perfLine += ` @ ${rpm} rpm`;
      }
      details.push(perfLine);
    }

    //  Add metrics
    for (const [key, value] of bucket.metrics) {
      if (key.toLowerCase().includes('warranty')) {
        details.push(`${this.COMMON.WARRANTY_PREFIX}${value}`);
      } else if (key.toLowerCase().includes('range')) {
        details.push(`${this.COMMON.RANGE_PREFIX}${value}`);
      } else {
        details.push(formatDetail(key, value));
      }
    }

    //Add important extras that didn't fit other categories
    const importantExtras = bucket.extras.filter((extra) => {
      const val = extra.value.toLowerCase();
      return val !== this.COMMON.STANDARD && val !== this.COMMON.NO && val.length > 0;
    });

    for (const extra of importantExtras) {
      const formattedKey = extra.attribute
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
        const lowerStringValue = String(extra.value).toLowerCase();
        lowerStringValue === this.COMMON.YES ? details.push(`${formattedKey}`) : details.push(`${formattedKey}: ${extra.value}`);
    }

    // If details still empty and presence==true, add "Standard feature"
    if (details.length === 0 && bucket.presence) {
      details.push(this.COMMON.STANDARD_FEATURE);
    }

    return details;
  }

  //  Calculate feature score
  private calculateScore(bucket: FeatureBucket): number {
    let score = 0;
    if (bucket.brand) score += 3;
    if (bucket.type && bucket.type !== this.COMMON.STANDARD) score += 2;
    if (bucket.capabilities.size > 0) score += 2;
    if (bucket.specs.size > 0) score += 2;
    if (bucket.performance.size > 0 || bucket.metrics.size > 0) score += 1;
    return score;
  }

  public convertEquipmentData(vehicleData: VehicleData): GroupedFeatures {
    const equipments = vehicleData.bumperResponse.vehicles.automobiles[0]?.equipments || [];
    const buckets = new Map<string, FeatureBucket>();

    for (const equipment of equipments) {
      const location = equipment.location.trim();
      const locSuffix = location && location !== this.COMMON.UNKNOWN ? `${this.COMMON.SEPARATOR}${location}` : this.COMMON.EMPTY;
      const bucketKey = `${equipment.group}${this.COMMON.SEPARATOR}${equipment.name}${locSuffix}`;

      if (!buckets.has(bucketKey)) {
        buckets.set(bucketKey, this.createFeatureBucket());
      }

      const bucket = buckets.get(bucketKey)!;
      this.ingestAttribute(bucket, equipment);
    }

    const groupedFeatures: GroupedFeatures = {};

    for (const [bucketKey, bucket] of buckets) {
      const [group, name] = bucketKey.split(this.COMMON.SEPARATOR);

      const title = this.synthesizeTitle(name, bucket);
      const details = this.synthesizeDetails(bucket);

      //Skip if no meaningful content
      if (
        details.length === 0 ||
        (details.length === 1 && details[0] === this.COMMON.STANDARD_FEATURE && !bucket.brand && !bucket.type)
      ) {
        continue;
      }

      if (!groupedFeatures[group]) {
        groupedFeatures[group] = [];
      }

      const score = this.calculateScore(bucket);
      groupedFeatures[group].push({
        title,
        details,
        score,
      });
    }

    // Sort features within each category
    for (const group in groupedFeatures) {
      groupedFeatures[group].sort((a, b) => {
        if (a.score !== b.score) {
          return b.score - a.score;
        }
        return a.title.localeCompare(b.title);
      });
    }

    return groupedFeatures;
  }


  public formatOutput(groupedFeatures: GroupedFeatures): string {
    let output = this.COMMON.EMPTY;
  
    // Blacklist configuration
    const BLACKLIST_GROUPS = ['Dimensions', 'Suspension','General','Fuel Economy'];
    const BLACKLIST_TITLES = ['Emission control level','Crash test results'];
  
    const BLACKLIST_TITLES_SUBSTRINGS = ['Crash test results','Axle ratio'];
  
    const normalizeText = (text: string): string => {
      return text.toLowerCase().replace(/[^a-z0-9]/g, this.COMMON.EMPTY);
    };
  
    const isBlacklisted = (groupName: string, featureTitle: string): boolean => {

      if (BLACKLIST_GROUPS.includes(groupName)) {
        return true;
      }
      if (BLACKLIST_TITLES.includes(featureTitle)) {
        return true;
      }
      
      if (BLACKLIST_TITLES_SUBSTRINGS.some(substring => featureTitle.startsWith(substring))) {
        return true;
      }
      
      return false;
    };
    
    const orderedGroups = this.CATEGORY_ORDER.filter(
      (group) => groupedFeatures[group] && !BLACKLIST_GROUPS.includes(group)
    );
  
    for (const group of orderedGroups) {
      const filteredFeatures = groupedFeatures[group].filter(
        (feature) => !isBlacklisted(group, feature.title)
      );
  
      // Skip group if no features remain after filtering
      if (filteredFeatures.length === 0) {
        continue;
      }
  
      output += `${group}\n`;
      output += '-'.repeat(group.length) + '\n';
  
      for (const feature of filteredFeatures) {
        // Check if there's only one detail and it contains the title information
        if (feature.details.length === 1) {
          const detail = feature.details[0];
          const titleLower = normalizeText(feature.title);
          const detailLower = normalizeText(detail);
  
          // If the detail already contains the title (or starts with it), just use the detail
          if (detailLower.includes(titleLower) || detailLower.startsWith(titleLower)) {
            output += `• ${detail}\n\n`;
            continue;
          }
        }
  
        output += `• ${feature.title}\n`;
        for (const detail of feature.details) {
          output += `  - ${detail}\n`;
        }
        output += '\n';
      }
    }
  
    return output;
  }
}

export function processVehicleData(jsonData: VehicleData): string {
  const converter = new VehicleEquipmentConverter();
  const groupedFeatures = converter.convertEquipmentData(jsonData);
  return converter.formatOutput(groupedFeatures);
}
